import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { TenantStorageUsage } from "../../../../api/graphql";
import { TenantStorageUsageListStore } from "../../store";

import { VisibilityIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: TenantStorageUsageListStore };

export const TenantStorageUsageListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<TenantStorageUsage>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: TenantStorageUsage, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<TenantStorageUsage>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "operation") {
          return {
            ...col,
            id: "operation",
            accessorFn: (row: TenantStorageUsage) => {
              const v = (row as any).operation;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.StorageOperationOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "timestamp") {
          return {
            ...col,
            id: "timestamp",
            accessorFn: (row: TenantStorageUsage) =>
              row.timestamp
                ? new Date(row.timestamp).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns]);

    const rows = store.tenantStorageUsageCurrentPage || [];

    return (
      <div id={"ListOfTenantStorageUsage"}>
        <div id={"StartOfListingOfTenantStorageUsage"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
